<template>
  <div class="fares-panel table-container">
    <!-- Cabecera estándar: misma disposición que el tarifario de clientes -->
    <div class="table-header">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
        <div class="d-flex mb-2 mb-md-0">
          <button
            type="button"
            :class="['button', direction === 'OUT' ? 'button-primary' : 'button-light']"
            @click="setDirection('OUT')"
          >
            Distrito → Aeropuerto
          </button>
          <button
            type="button"
            :class="['button ml-2', direction === 'IN' ? 'button-primary' : 'button-light']"
            @click="setDirection('IN')"
          >
            Aeropuerto → Distrito
          </button>
        </div>

        <div class="d-flex align-items-center flex-wrap">
          <div class="d-none d-md-flex">
            <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover
              title="Recargar" @click="load()">
              <i class="ri-loop-right-line"></i>
            </button>
          </div>
          <SearchBar placeholder="Buscar distrito..." class="ml-md-2" @search="search = $event"
            @input="search = $event" />
          <b-button variant="none" class="button button-light ml-md-2 mt-2 mt-md-0" @click="openCopy">
            Copiar tarifas
          </b-button>
        </div>
      </div>
    </div>

    <!-- Acordeones por provincia -->
    <div class="tarifa-body">
      <template v-if="visibleProvinces.length">
        <div v-for="prov in visibleProvinces" :key="prov.name" class="prov-accordion">
          <button type="button" class="prov-header" @click="toggle(prov.name)">
            <i class="chevron" :class="isExpanded(prov) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
            <span class="prov-name">{{ titleCase(prov.name) }}</span>
            <span class="prov-count">({{ prov.total_districts }} distritos)</span>
            <span class="prov-badge badge" :class="prov.with_fare ? 'badge-green' : 'badge-yellow'">
              {{ prov.with_fare }} con tarifa
            </span>
          </button>

          <!-- custom-form habilita el estilo de custom-form-control en las celdas -->
          <div v-show="isExpanded(prov)" class="prov-body custom-form">
            <div class="table-responsive mb-0">
              <table class="custom-table w-100">
                <thead>
                  <tr>
                    <th class="col-district text-left">Distrito</th>
                    <th v-for="vt in vehicleTypes" :key="vt" class="col-price">{{ vt }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in visibleDistricts(prov)" :key="d.district_id">
                    <td class="col-district">
                      <span class="ubigeo">{{ d.ubigeo_inei || "—" }}</span>
                      <span class="dname">{{ d.name }}</span>
                    </td>
                    <td v-for="cell in d.cells" :key="cell.vehicle_type" class="col-price">
                      <ProviderFareCell
                        :provider-id="providerId"
                        :district-id="d.district_id"
                        :route-type="routeType"
                        :cell="cell"
                        @saved="onSaved(prov, d, $event)"
                      />
                    </td>
                  </tr>
                  <tr v-if="!visibleDistricts(prov).length">
                    <td :colspan="vehicleTypes.length + 1" class="text-center text-muted py-3">
                      Sin distritos que coincidan.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
      <div v-else-if="!loading" class="tarifa-empty">
        <template v-if="search">No se encontró ningún distrito con ese nombre.</template>
        <template v-else>No hay distritos activos en esta ciudad.</template>
      </div>
    </div>

    <div class="tarifa-footer">
      {{ summary.with_fare }} de {{ summary.total_districts }} distritos con tarifa
      <span v-if="summary.negative_margin" class="text-danger ml-2">
        · {{ summary.negative_margin }} con margen negativo
      </span>
    </div>

    <!-- Copiar tarifas -->
    <b-modal id="copy-fares-modal" title="Copiar tarifas de otro proveedor" hide-footer size="md">
      <div class="custom-form">
        <div class="form-group">
          <label class="label">Proveedor origen</label>
          <select class="custom-form-control" v-model="copySourceId" @change="loadCopyPreview">
            <option :value="null">Elegir proveedor</option>
            <option v-for="p in copyOptions" :key="p.id" :value="p.id">
              {{ p.business_name || p.contact_name }}
            </option>
          </select>
        </div>

        <div v-if="copyPreview" class="copy-preview">
          <p class="m-0">
            Se copiarán <strong>{{ copyPreview.to_copy }}</strong> tarifas.
          </p>
          <p v-if="copyPreview.to_overwrite" class="m-0 text-danger">
            <i class="ri-alert-line"></i>
            <strong>{{ copyPreview.to_overwrite }}</strong> tarifas ya cargadas se van a sobrescribir.
          </p>
          <p v-if="!copyPreview.to_copy" class="m-0 text-muted">
            Ese proveedor no tiene tarifas cargadas.
          </p>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('copy-fares-modal')">
            Cancelar
          </b-button>
          <b-button
            variant="none"
            class="button button-primary"
            :disabled="!copyPreview || !copyPreview.to_copy || copying"
            @click="confirmCopy"
          >
            {{ copying ? "Copiando..." : "Copiar" }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, SearchBar } from "wize-admin";

import { ProviderService } from "@/core/services";
import ProviderFareCell from "./ProviderFareCell.vue";

// Tarifario de costo de un proveedor (DEF-ADM-020 §4.1). Misma estructura que el
// tarifario de clientes (DEF-ADM-017): pills de dirección, acordeones por
// provincia y edición inline con auto-guardado.
export default {
  name: "ProviderFaresPanel",
  components: { ProviderFareCell, SearchBar },
  props: {
    providerId: { type: [String, Number], required: true },
  },
  data() {
    return {
      direction: "OUT",
      routeType: "district_to_airport",
      vehicleTypes: [],
      provinces: [],
      summary: { total_districts: 0, with_fare: 0, negative_margin: 0 },
      search: "",
      loading: false,
      expanded: {},
      copyOptions: [],
      copySourceId: null,
      copyPreview: null,
      copying: false,
    };
  },
  computed: {
    visibleProvinces() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.provinces;
      return this.provinces.filter((p) =>
        p.districts.some((d) => d.name.toLowerCase().includes(q))
      );
    },
  },
  watch: {
    providerId() {
      this.load();
    },
  },
  methods: {
    titleCase(s) {
      return (s || "")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    // Buscando, se expande toda provincia con coincidencias; si no, manda el
    // estado manual del acordeón.
    isExpanded(prov) {
      if (this.search.trim()) return true;
      return !!this.expanded[prov.name];
    },
    toggle(name) {
      this.$set(this.expanded, name, !this.expanded[name]);
    },
    visibleDistricts(prov) {
      const q = this.search.trim().toLowerCase();
      if (!q) return prov.districts;
      return prov.districts.filter((d) => d.name.toLowerCase().includes(q));
    },
    setDirection(dir) {
      if (this.direction === dir) return;
      this.direction = dir;
      this.load();
    },
    async load() {
      this.loading = true;
      try {
        const res = await ProviderService.fares(this.providerId, this.direction);
        const p = res.payload;
        this.routeType = p.route_type;
        this.vehicleTypes = p.vehicle_types || [];
        this.provinces = p.provinces || [];
        this.summary = p.summary || { total_districts: 0, with_fare: 0, negative_margin: 0 };
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo cargar el tarifario");
      } finally {
        this.loading = false;
      }
    },
    // Recalcula margen y contadores en memoria: evita recargar toda la grilla
    // en cada celda editada.
    onSaved(prov, district, { vehicleType, amount }) {
      const cell = district.cells.find((c) => c.vehicle_type === vehicleType);
      if (!cell) return;

      const hadFare = district.cells.some((c) => c.provider_amount != null);
      const wasNegative = cell.margin != null && cell.margin <= 0;

      cell.provider_amount = amount;
      if (amount != null && cell.client_amount != null) {
        cell.margin = Number((cell.client_amount - amount).toFixed(2));
        cell.margin_percent = cell.client_amount > 0 ? (cell.margin / cell.client_amount) * 100 : null;
      } else {
        cell.margin = null;
        cell.margin_percent = null;
      }

      const hasFare = district.cells.some((c) => c.provider_amount != null);
      if (hadFare !== hasFare) {
        const delta = hasFare ? 1 : -1;
        prov.with_fare += delta;
        this.summary.with_fare += delta;
      }
      const isNegative = cell.margin != null && cell.margin <= 0;
      if (wasNegative !== isNegative) {
        this.summary.negative_margin += isNegative ? 1 : -1;
      }
    },
    async openCopy() {
      this.copySourceId = null;
      this.copyPreview = null;
      try {
        const res = await ProviderService.list({ status: 1 }, { page: 0, size: 200 });
        this.copyOptions = (res.payload.items || []).filter(
          (p) => String(p.id) !== String(this.providerId)
        );
      } catch (ex) {
        console.error(ex);
        this.copyOptions = [];
      }
      this.$bvModal.show("copy-fares-modal");
    },
    async loadCopyPreview() {
      this.copyPreview = null;
      if (!this.copySourceId) return;
      try {
        const res = await ProviderService.copyFaresPreview(this.providerId, this.copySourceId);
        this.copyPreview = res.payload;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo revisar las tarifas a copiar");
      }
    },
    async confirmCopy() {
      this.copying = true;
      try {
        const res = await ProviderService.copyFares(this.providerId, this.copySourceId);
        Alert.success(`Se copiaron ${res.payload.copied} tarifas`);
        this.$bvModal.hide("copy-fares-modal");
        await this.load();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron copiar las tarifas");
      } finally {
        this.copying = false;
      }
    },
  },
  async mounted() {
    await this.load();
  },
};
</script>

<style lang="stylus" scoped>
// El acordeón y el footer replican los del tarifario de clientes (Tarifas →
// Precios base), para que ambas pantallas se vean iguales. El resto —cabecera,
// tabla, badges e inputs— usa las clases de wize-admin.
.tarifa-body
  padding 12px
.prov-accordion
  border 1px solid #e5e7eb
  border-radius 8px
  margin-bottom 10px
  overflow hidden
.prov-header
  width 100%
  display flex
  align-items center
  gap 10px
  background #f7f8fa
  border none
  padding 12px 14px
  cursor pointer
  text-align left
  .chevron
    font-size 18px
    color #6c757d
.prov-name
  font-weight 600
  font-size 14px
.prov-count
  font-size 12px
  color #6c757d
// El badge de completitud usa las clases del sistema (badge-green /
// badge-yellow); aquí solo se empuja a la derecha.
.prov-badge
  margin-left auto
.prov-body
  padding 0
.custom-table
  .col-price
    width 130px
  td.col-price
    padding-top 7px
    padding-bottom 7px
  .col-district
    width 220px
    vertical-align middle
    .ubigeo
      font-size 11px
      color #adb5bd
      margin-right 8px
    .dname
      font-size 13px
.tarifa-empty
  text-align center
  color #6c757d
  padding 40px 16px
  font-size 14px
.tarifa-footer
  padding 12px 16px
  border-top 0.3px solid #e5e7eb
  font-size 13px
  color #6c757d
.copy-preview
  background #f8f9fa
  border-radius 6px
  padding 12px
</style>
